import { defineStore } from "pinia";
import { computed, ref, watch } from "vue";

import {
  cancelBulkApply as cancelBulkApplyRequest,
  getActiveBulkApply,
  getBulkApplyStatus,
  resumeBulkApply as resumeBulkApplyRequest,
  startBulkApply as startBulkApplyRequest,
} from "@/api/tracking";
import { usePolling } from "@/composables/usePolling";
import { ApiError } from "@/types/api";
import type { Application } from "@/types/application";
import type { BulkApplyItemStatus, BulkApplyJob, BulkApplyMode } from "@/types/bulkApply";
import { sanitizeExternalHttpUrl } from "@/utils/externalUrl";

import { useApplicationsStore } from "@/stores/applications";

export type QueueItemState = "queued" | "running" | "manual" | "review_ready" | "failed" | "cancelled";

export interface ApplicationQueueItem {
  applicationId: string;
  company: string;
  roleTitle: string;
  location: string;
  state: QueueItemState;
  label: string;
  message: string;
  actionLabel: string;
  url: string;
}

const ACTIVE_JOB_STATUSES = new Set(["queued", "running", "paused_manual"]);
const TERMINAL_JOB_STATUSES = new Set(["completed", "failed", "cancelled"]);
const MANUAL_ITEM_STATUSES = new Set(["blocked", "manual", "manual_apply_required", "automation_blocked"]);
const REVIEW_READY_ITEM_STATUSES = new Set([
  "review_ready",
  "ready_for_user_submission",
  "needs_review",
  "review_application",
]);
const COMPLETED_ITEM_STATUSES = new Set(["review_ready", "failed", "cancelled"]);
const POLL_BACKOFF_MS = [2_000, 4_000, 8_000, 16_000] as const;
const QUEUE_STATE_LABELS: Record<QueueItemState, string> = {
  cancelled: "Cancelled",
  failed: "Failed",
  manual: "Manual apply required",
  queued: "Queued",
  review_ready: "Review Ready",
  running: "Applying",
};

function normalize(value: unknown): string {
  return String(value ?? "").trim().replace(/\s+/g, " ").toLocaleLowerCase();
}

function normalizeError(caught: unknown, fallback: string): ApiError {
  return caught instanceof ApiError ? caught : new ApiError(fallback);
}

function queueState(status: BulkApplyItemStatus | string | undefined): QueueItemState {
  const normalized = normalize(status).replace(/\s+/g, "_");
  if (MANUAL_ITEM_STATUSES.has(normalized)) return "manual";
  if (REVIEW_READY_ITEM_STATUSES.has(normalized)) return "review_ready";
  if (normalized === "running") return "running";
  if (normalized === "failed") return "failed";
  if (normalized === "cancelled") return "cancelled";
  return "queued";
}

function applicationUrl(application: Application | undefined): string {
  return String(application?.last_application_url || application?.application_url || application?.source_url || "");
}

function queueLabel(state: QueueItemState, waiting = false): string {
  return waiting ? "Queued - waiting" : QUEUE_STATE_LABELS[state];
}

function queueMessage(value: unknown): string {
  const message = String(value || "").trim();
  return /\b(?:final\s+submit|submit\s+application)\b/i.test(message) ? "" : message;
}

function isTerminalJob(job: BulkApplyJob | null): boolean {
  return Boolean(job && TERMINAL_JOB_STATUSES.has(job.status));
}

export const useBulkApplyStore = defineStore("bulkApply", () => {
  const applicationsStore = useApplicationsStore();
  const bulkMode = ref<BulkApplyMode>("user_final_review");
  const activeBulkJob = ref<BulkApplyJob | null>(null);
  const bulkMutationInFlight = ref(false);
  const bulkPollFailures = ref(0);
  const bulkError = ref<ApiError | null>(null);
  let sessionGeneration = 0;
  let observationActive = true;
  let mutationToken: symbol | null = null;
  let lastRefreshedTerminalJobId: string | null = null;
  let terminalRefreshInFlight: {
    controller: AbortController;
    jobId: string;
    promise: Promise<void>;
  } | null = null;
  let activeRestoreInFlight: {
    controller: AbortController;
    promise: Promise<BulkApplyJob | null>;
  } | null = null;

  const hasActiveBulkJob = computed(() => Boolean(activeBulkJob.value && ACTIVE_JOB_STATUSES.has(activeBulkJob.value.status)));
  const canStartBulkApply = computed(
    () => applicationsStore.selectedApplicationIds.length > 0
      && !activeBulkJob.value
      && !bulkMutationInFlight.value,
  );
  const applicationById = computed(
    () => new Map(applicationsStore.applications.map((application) => [application.id, application])),
  );
  const previewQueue = computed<ApplicationQueueItem[]>(() =>
    applicationsStore.selectedApplicationIds.map((applicationId) => {
      const application = applicationById.value.get(applicationId);
      const state = queueState(application?.status);
      return {
        applicationId,
        company: String(application?.company ?? ""),
        roleTitle: String(application?.role_title ?? ""),
        location: String(application?.location ?? ""),
        state,
        label: queueLabel(state),
        message: "",
        actionLabel: sanitizeExternalHttpUrl(applicationUrl(application)) ? "Open portal" : "",
        url: sanitizeExternalHttpUrl(applicationUrl(application)),
      };
    }),
  );
  const queueItems = computed<ApplicationQueueItem[]>(() => {
    if (!activeBulkJob.value) return previewQueue.value;
    const job = activeBulkJob.value;
    return job.results.map((result, index) => {
      const application = applicationById.value.get(result.application_id);
      const state = queueState(result.status);
      const waiting = job.status === "paused_manual" && state === "queued" && index > (job.current_index ?? -1);
      const url = sanitizeExternalHttpUrl(String(result.action_url || ""))
        || sanitizeExternalHttpUrl(applicationUrl(application));
      return {
        applicationId: result.application_id,
        company: String(result.company || application?.company || ""),
        roleTitle: String(result.role_title || application?.role_title || ""),
        location: String(result.location || application?.location || ""),
        state,
        label: queueLabel(state, waiting),
        message: queueMessage(result.message),
        actionLabel: url ? (state === "review_ready" ? "Review application" : "Open portal") : "",
        url,
      };
    });
  });
  const queueCompletedCount = computed(() =>
    activeBulkJob.value
      ? activeBulkJob.value.results.filter((result) => COMPLETED_ITEM_STATUSES.has(queueState(result.status))).length
      : 0,
  );
  const queueProgressPercent = computed(() => {
    const total = activeBulkJob.value?.results.length ?? 0;
    return total ? Math.round((queueCompletedCount.value / total) * 100) : 0;
  });
  const queueStatusMessage = computed(() => {
    const job = activeBulkJob.value;
    if (!job) return previewQueue.value.length ? "Ready to start" : "Select roles to apply";
    if (job.status === "paused_manual") return "Queued - waiting";
    if (job.status === "completed") return "Completed";
    if (job.status === "failed") return "Bulk apply failed";
    if (job.status === "cancelled") return "Cancelled";
    if (job.status === "running") return "Applying";
    return "Queued";
  });

  function pollingDelay(job: BulkApplyJob): number | null {
    if (!new Set(["queued", "running"]).has(job.status)) return null;
    const delay = job.controls.poll_after_ms;
    if (delay === null || !Number.isInteger(delay) || delay < 0) return 2_000;
    return Math.min(delay, 30_000);
  }

  function requestedJobId(): string {
    const jobId = activeBulkJob.value?.id;
    if (!jobId) throw new ApiError("No bulk apply job is active.");
    return jobId;
  }

  async function refreshTerminalApplications(jobId: string, session: number): Promise<void> {
    if (lastRefreshedTerminalJobId === jobId) return;
    if (terminalRefreshInFlight?.jobId === jobId) return terminalRefreshInFlight.promise;
    const record = {
      controller: new AbortController(),
      jobId,
      promise: Promise.resolve() as Promise<void>,
    };
    record.promise = (async () => {
      try {
        await applicationsStore.load(record.controller.signal);
        if (
          sessionGeneration === session &&
          terminalRefreshInFlight === record &&
          !record.controller.signal.aborted
        ) {
          lastRefreshedTerminalJobId = jobId;
        }
      } catch (caught: unknown) {
        if (
          sessionGeneration === session &&
          terminalRefreshInFlight === record &&
          !record.controller.signal.aborted &&
          !(caught instanceof Error && caught.name === "AbortError")
        ) {
          bulkError.value = normalizeError(caught, "Applications could not be refreshed.");
        }
      } finally {
        if (terminalRefreshInFlight === record) terminalRefreshInFlight = null;
      }
    })();
    terminalRefreshInFlight = record;
    return record.promise;
  }

  async function acceptBulkJob(job: BulkApplyJob, source: "poll" | "mutation", expectedId?: string): Promise<boolean> {
    if (expectedId && job.id !== expectedId) return false;
    activeBulkJob.value = job;
    bulkPollFailures.value = 0;
    bulkError.value = null;
    const session = sessionGeneration;
    if (isTerminalJob(job)) {
      polling.stop();
      await refreshTerminalApplications(job.id, session);
      return true;
    }
    const delay = pollingDelay(job);
    if (delay === null) {
      polling.stop();
      return true;
    }
    if (source === "mutation" && observationActive) polling.start(delay);
    return true;
  }

  async function acceptPolledJob(job: BulkApplyJob): Promise<void> {
    const activeId = activeBulkJob.value?.id;
    if (!activeId || job.id !== activeId) return;
    await acceptBulkJob(job, "poll", activeId);
  }

  const polling = usePolling<BulkApplyJob>({
    operation: (signal) => getBulkApplyStatus(requestedJobId(), signal),
    onResult: acceptPolledJob,
    onFailure: (caught, failures) => {
      bulkPollFailures.value = failures;
      bulkError.value = normalizeError(caught, "Bulk apply status could not be refreshed.");
    },
    nextDelay: () => (activeBulkJob.value ? pollingDelay(activeBulkJob.value) : null),
    retryDelay: (_caught, failures) =>
      failures < 5 ? (POLL_BACKOFF_MS[Math.min(failures - 1, 3)] ?? 16_000) : null,
  });

  function ownsMutation(token: symbol, session: number): boolean {
    return mutationToken === token && sessionGeneration === session;
  }

  function resumeObservation(): void {
    const retained = activeBulkJob.value;
    if (!observationActive || !retained) return;
    const delay = pollingDelay(retained);
    if (delay !== null) polling.start(delay);
  }

  function clearTerminalJob(): void {
    if (!isTerminalJob(activeBulkJob.value)) return;
    polling.stop();
    activeBulkJob.value = null;
    bulkPollFailures.value = 0;
    bulkError.value = null;
  }

  async function startBulkApply(): Promise<BulkApplyJob | null> {
    if (bulkMutationInFlight.value || activeBulkJob.value) return activeBulkJob.value;
    const applicationIds = [...applicationsStore.selectedApplicationIds];
    if (!applicationIds.length) {
      const missingSelection = new ApiError("Select at least one role to apply.");
      bulkError.value = missingSelection;
      throw missingSelection;
    }
    const mode = bulkMode.value;
    const optimisticJob: BulkApplyJob = {
      id: `optimistic-${Date.now()}`,
      status: "queued",
      mode,
      application_ids: applicationIds,
      results: applicationIds.map((applicationId) => ({ application_id: applicationId, label: "Queued", status: "queued" })),
      controls: { can_cancel: false, can_resume: false, poll_after_ms: null },
    };
    const token = Symbol("start");
    const session = sessionGeneration;
    polling.stop();
    mutationToken = token;
    activeBulkJob.value = optimisticJob;
    bulkMutationInFlight.value = true;
    bulkError.value = null;
    try {
      const job = await startBulkApplyRequest(applicationIds, mode);
      if (!ownsMutation(token, session)) return null;
      await acceptBulkJob(job, "mutation");
      return job;
    } catch (caught: unknown) {
      const normalized = normalizeError(caught, "Bulk apply could not be started.");
      if (ownsMutation(token, session)) {
        if (activeBulkJob.value?.id === optimisticJob.id) activeBulkJob.value = null;
        bulkError.value = normalized;
      }
      throw normalized;
    } finally {
      if (ownsMutation(token, session)) {
        mutationToken = null;
        bulkMutationInFlight.value = false;
      }
    }
  }

  async function runExistingMutation(
    request: (jobId: string) => Promise<BulkApplyJob>,
    fallback: string,
  ): Promise<BulkApplyJob | null> {
    const jobId = activeBulkJob.value?.id;
    if (!jobId || bulkMutationInFlight.value) return activeBulkJob.value;
    const token = Symbol(jobId);
    const session = sessionGeneration;
    polling.stop();
    mutationToken = token;
    bulkMutationInFlight.value = true;
    bulkError.value = null;
    try {
      const job = await request(jobId);
      if (!ownsMutation(token, session)) return null;
      if (!(await acceptBulkJob(job, "mutation", jobId))) {
        if (ownsMutation(token, session)) resumeObservation();
        return activeBulkJob.value;
      }
      return job;
    } catch (caught: unknown) {
      const normalized = normalizeError(caught, fallback);
      if (ownsMutation(token, session)) {
        bulkError.value = normalized;
        resumeObservation();
      }
      throw normalized;
    } finally {
      if (ownsMutation(token, session)) {
        mutationToken = null;
        bulkMutationInFlight.value = false;
      }
    }
  }

  function resumeBulkApply(): Promise<BulkApplyJob | null> {
    return runExistingMutation(resumeBulkApplyRequest, "Bulk apply could not be resumed.");
  }

  function cancelBulkApply(): Promise<BulkApplyJob | null> {
    return runExistingMutation(cancelBulkApplyRequest, "Bulk apply could not be cancelled.");
  }

  async function refreshBulkApplyStatus(): Promise<BulkApplyJob | null> {
    if (!observationActive || !activeBulkJob.value || bulkMutationInFlight.value) return activeBulkJob.value;
    try {
      await polling.runNow();
    } catch {
      return activeBulkJob.value;
    }
    return activeBulkJob.value;
  }

  async function retryBulkApplyStatus(): Promise<BulkApplyJob | null> {
    if (!observationActive || bulkMutationInFlight.value) return activeBulkJob.value;
    polling.stop();
    bulkPollFailures.value = 0;
    bulkError.value = null;
    if (!activeBulkJob.value) return restoreActiveBulkApply();
    return refreshBulkApplyStatus();
  }

  async function restoreActiveBulkApply(): Promise<BulkApplyJob | null> {
    if (activeBulkJob.value) return activeBulkJob.value;
    if (activeRestoreInFlight) return activeRestoreInFlight.promise;
    const session = sessionGeneration;
    const record = {
      controller: new AbortController(),
      promise: Promise.resolve(null) as Promise<BulkApplyJob | null>,
    };
    record.promise = (async () => {
      try {
        const job = await getActiveBulkApply(record.controller.signal);
        if (
          !job ||
          !observationActive ||
          sessionGeneration !== session ||
          record.controller.signal.aborted
        ) {
          return null;
        }
        await acceptBulkJob(job, "mutation");
        return job;
      } catch (caught: unknown) {
        if (
          observationActive &&
          sessionGeneration === session &&
          !record.controller.signal.aborted &&
          !(caught instanceof Error && caught.name === "AbortError")
        ) {
          bulkError.value = normalizeError(caught, "The active bulk apply queue could not be restored.");
        }
        return null;
      } finally {
        if (activeRestoreInFlight === record) activeRestoreInFlight = null;
      }
    })();
    activeRestoreInFlight = record;
    return record.promise;
  }

  function activate(): void {
    observationActive = true;
    const job = activeBulkJob.value;
    if (!job) {
      void restoreActiveBulkApply();
      return;
    }
    const delay = pollingDelay(job);
    if (delay !== null) polling.start(delay);
  }

  function dispose(): void {
    observationActive = false;
    polling.stop();
    activeRestoreInFlight?.controller.abort();
    activeRestoreInFlight = null;
  }

  function reset(): void {
    sessionGeneration += 1;
    observationActive = false;
    mutationToken = null;
    polling.stop();
    terminalRefreshInFlight?.controller.abort();
    activeRestoreInFlight?.controller.abort();
    bulkMode.value = "user_final_review";
    activeBulkJob.value = null;
    bulkMutationInFlight.value = false;
    bulkPollFailures.value = 0;
    bulkError.value = null;
    lastRefreshedTerminalJobId = null;
    terminalRefreshInFlight = null;
    activeRestoreInFlight = null;
  }

  watch(() => applicationsStore.selectedApplicationIds, clearTerminalJob, { flush: "sync" });
  watch(bulkMode, clearTerminalJob, { flush: "sync" });

  return {
    bulkMode,
    activeBulkJob,
    bulkMutationInFlight,
    bulkPollFailures,
    bulkError,
    hasActiveBulkJob,
    canStartBulkApply,
    previewQueue,
    queueItems,
    queueCompletedCount,
    queueProgressPercent,
    queueStatusMessage,
    startBulkApply,
    resumeBulkApply,
    cancelBulkApply,
    refreshBulkApplyStatus,
    retryBulkApplyStatus,
    restoreActiveBulkApply,
    activate,
    dispose,
    reset,
  };
});
